import { useMemo, useRef, useState, useEffect } from 'react';
import { LAB_STEPS } from '../../content/lessons';
import { PRESETS } from '../../content/presets';
import type { ControlKey } from '../../content/types';
import { simClient } from '../../simulation/client';
import type { ControllerKind } from '../../simulation/types';
import { describeController, FF_LABELS } from '../../state/runRecord';
import { useApp } from '../../state/store';
import { Term } from '../Term';
import { parseMarkup } from '../Markup';
import { ControlsPanel } from './ControlsPanel';
import { DataTable } from './DataTable';
import { MetricsPanel } from './MetricsPanel';
import { ComparePlots, LivePlots } from './Plots';
import { RunsPanel } from './RunsPanel';
import { Scene } from './Scene';
import { usePrefersReducedMotion, useSim, useThrottled } from './useSim';

const SPEEDS = [0.25, 0.5, 1, 2, 4];

export interface SimMode {
  controls: Set<ControlKey | 'all'>;
  kinds: ControllerKind[];
}

export function statusText(status: string, playing: boolean, t: number, stop: string | null) {
  if (stop) return `Stopped at t = ${t.toFixed(2)} s`;
  if (status === 'complete') return 'Run complete';
  if (playing) return 'Running';
  if (t === 0) return 'Ready (paused at the start)';
  return `Paused at t = ${t.toFixed(2)} s`;
}

export function SimulatorPanel({ mode }: { mode: SimMode }) {
  const sim = useSim();
  const experiment = useApp((s) => s.experiment);
  const prefs = useApp((s) => s.prefs);
  const setPrefs = useApp((s) => s.setPrefs);
  const highlight = useApp((s) => s.highlight);
  const highlightSticky = useApp((s) => s.highlightSticky);
  const setHighlight = useApp((s) => s.setHighlight);
  const resetRun = useApp((s) => s.resetRun);
  const replay = useApp((s) => s.replay);
  const loadSavedRun = useApp((s) => s.loadSavedRun);
  const comparison = useApp((s) => s.comparison);
  const runs = useApp((s) => s.runs);
  const evidence = useApp((s) => s.progress.evidence);
  const reducedMotion = usePrefersReducedMotion(prefs.reducedMotion);
  const traceVersion = useThrottled(sim.traceVersion, sim.playing ? 60 : 0);
  const [view, setView] = useState<'live' | 'compare'>('live');
  const compareRuns = useMemo(() => comparison.map((id) => runs.find((r) => r.id === id)).filter((r): r is NonNullable<typeof r> => !!r), [comparison, runs]);

  useEffect(() => {
    simClient.speed = prefs.speed;
  }, [prefs.speed]);
  useEffect(() => {
    if (view === 'compare' && compareRuns.length === 0) setView('live');
  }, [view, compareRuns.length]);

  // Announce status transitions (not every frame) to assistive technology.
  const announce = statusText(sim.status, sim.playing, sim.t, sim.stopReason?.message ?? null);
  const lastAnnounce = useRef('');
  const [live, setLive] = useState('');
  useEffect(() => {
    if (announce !== lastAnnounce.current) {
      lastAnnounce.current = announce;
      setLive(sim.stopReason ? `${announce}. ${sim.stopReason.message}` : announce);
    }
  }, [announce, sim.stopReason]);

  if (!experiment) return null;
  const config = experiment.config;
  const preset = PRESETS[experiment.presetId];
  const step = experiment.labStepId ? LAB_STEPS[experiment.labStepId]?.step : null;
  const recordedRunId = experiment.labStepId ? evidence[experiment.labStepId] : null;
  const recordedRun = recordedRunId ? runs.find((r) => r.id === recordedRunId) : null;
  const done = sim.status === 'complete' || sim.status === 'stopped';
  const ready = sim.engine === 'ready';
  const latest = sim.latest;
  const showDamping = mode.controls.has('all') || mode.controls.has('damping-analysis');
  const showContrib = mode.controls.has('all') || mode.controls.has('contributions');

  return (
    <div className="sim" onMouseLeave={() => highlight && !highlightSticky && setHighlight(null)}>
      <div className="sim-status" aria-label="Experiment status">
        <div>
          <span className="eyebrow">{step ? 'Lab step' : 'Free exploration'}</span>
          <h2 className="sim-title">{step ? parseMarkup(step.title) : preset?.title}</h2>
          {step && (
            <p className="small">
              {recordedRun ? (
                <span className="ok-text">Run {recordedRun.number}</span>
              ) : (
                <span className="muted">No run recorded yet</span>
              )}
            </p>
          )}
          <p className="small muted">
            {preset?.title} · {describeController(config.controller)} · {FF_LABELS[config.controller.ffMode]}
          </p>
        </div>
        <div className={`pill ${sim.stopReason ? 'warn' : done ? 'ok' : sim.playing ? 'run' : ''}`}>
          {statusText(sim.status, sim.playing, sim.t, sim.stopReason?.message ?? null)}
        </div>
      </div>
      <p className="sr-only" role="status" aria-live="polite">
        {live}
      </p>

      {sim.engine === 'loading' && <div className="notice info">Loading the physics engine (MuJoCo WebAssembly)…</div>}
      {sim.engine === 'failed' && (
        <div className="notice error" role="alert">
          {sim.engineMessage} <button className="btn small" onClick={() => window.location.reload()}>Reload</button>
        </div>
      )}
      {sim.error && (
        <div className="notice error" role="alert">
          {sim.error}
        </div>
      )}
      {sim.stopReason && (
        <div className="notice warn" role="note">
          {sim.stopReason.message} Press <strong>Reset</strong> to start again from the initial conditions.
        </div>
      )}

      <div className="sim-top">
        <div className="scene-wrap">
          <Scene config={config} sample={latest} highlight={highlight} reducedMotion={reducedMotion} playing={sim.playing} maxAltitude={sim.stats?.maxAltitude ?? 0} />
        </div>
        <div className="sim-side">
          <div className="transport" role="group" aria-label="Simulation controls">
            {sim.playing ? (
              <button type="button" className="btn primary" onClick={() => simClient.pause()}>
                ❚❚ Pause
              </button>
            ) : (
              <button type="button" className="btn primary" disabled={!ready || done} onClick={() => simClient.play()}>
                ▶ Play
              </button>
            )}
            <button type="button" className="btn" disabled={!ready || done} onClick={() => simClient.step()} title="Advance one time step (0.005 s)">
              Step Δt
            </button>
            <button type="button" className="btn" disabled={!ready || done} onClick={() => simClient.runToEnd()}>
              Run to end
            </button>
            <button type="button" className="btn" disabled={!ready} onClick={resetRun}>
              ↺ Reset
            </button>
            <button type="button" className="btn" disabled={!ready || sim.t === 0} onClick={replay} title="Rerun this exact experiment, including your manual inputs">
              Replay
            </button>
            <label className="speed">
              Speed{' '}
              <select value={prefs.speed} onChange={(e) => setPrefs({ speed: Number(e.target.value) })}>
                {SPEEDS.map((s) => (
                  <option key={s} value={s}>
                    {s}×
                  </option>
                ))}
              </select>
            </label>
            <label className="speed">
              Load run{' '}
              <select
                aria-label="Load a saved run"
                value=""
                disabled={!runs.length}
                onChange={(e) => {
                  const id = e.target.value;
                  if (id) loadSavedRun(id);
                }}
              >
                <option value="">{runs.length ? 'Choose…' : 'No saved runs'}</option>
                {[...runs].reverse().map((r) => (
                  <option key={r.id} value={r.id}>
                    Run {r.number} · {PRESETS[r.presetId]?.title ?? r.presetId}
                    {r.labStepId && LAB_STEPS[r.labStepId] ? ` · ${LAB_STEPS[r.labStepId].step.title}` : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <dl className="readout" aria-label="Current values">
            <div>
              <dt>
                <Term id="t" />
              </dt>
              <dd>
                {sim.t.toFixed(2)} / {config.duration} s
              </dd>
            </div>
            <div>
              <dt>
                <Term id="z" />
              </dt>
              <dd>{(latest?.z ?? config.initial.z).toFixed(3)} m</dd>
            </div>
            <div>
              <dt>
                <Term id="v" />
              </dt>
              <dd>{(latest?.v ?? 0).toFixed(3)} m/s</dd>
            </div>
            <div>
              <dt>
                <Term id="a" />
              </dt>
              <dd>{(latest?.a ?? 0).toFixed(3)} m/s²</dd>
            </div>
            <div>
              <dt>
                <Term id="error" />
              </dt>
              <dd>{(latest?.e ?? 0).toFixed(3)} m</dd>
            </div>
            <div>
              <dt>
                <Term id="Tcmd" />
              </dt>
              <dd>{(latest?.Tcmd ?? 0).toFixed(2)} N</dd>
            </div>
            <div>
              <dt>
                <Term id="T" />
              </dt>
              <dd>
                {(latest?.T ?? 0).toFixed(2)} N
                <span className="sat-flag" data-on={latest?.sat ? '' : undefined} aria-hidden={!latest?.sat}>
                  {' '}
                  (saturated)
                </span>
              </dd>
            </div>
          </dl>
          <ControlsPanel config={config} controls={mode.controls} kinds={mode.kinds} />
        </div>
      </div>

      <div className="plots-head">
        <div role="tablist" aria-label="Plot view" className="seg">
          <button role="tab" aria-selected={view === 'live'} className={view === 'live' ? 'on' : ''} onClick={() => setView('live')}>
            Current run
          </button>
          <button
            role="tab"
            aria-selected={view === 'compare'}
            className={view === 'compare' ? 'on' : ''}
            disabled={compareRuns.length === 0}
            onClick={() => setView('compare')}
          >
            Compare saved ({compareRuns.length})
          </button>
        </div>
        {view === 'live' && (
          <div className="row gap-s">
            <label className="check small">
              <input type="checkbox" checked={prefs.showAcceleration} onChange={(e) => setPrefs({ showAcceleration: e.target.checked })} /> Show acceleration
            </label>
            {showContrib && (
              <label className="check small">
                <input type="checkbox" checked={prefs.showContributions} onChange={(e) => setPrefs({ showContributions: e.target.checked })} /> Controller contributions
              </label>
            )}
          </div>
        )}
      </div>
      {view === 'live' ? (
        <LivePlots
          config={config}
          trace={simClient.trace}
          traceVersion={traceVersion}
          showAcceleration={prefs.showAcceleration}
          showContributions={prefs.showContributions && showContrib}
        />
      ) : (
        <ComparePlots runs={compareRuns} />
      )}

      <MetricsPanel
        config={config}
        presetId={experiment.presetId}
        trace={simClient.trace}
        traceVersion={traceVersion}
        stats={sim.stats}
        done={done}
        status={sim.status}
        showDamping={showDamping}
      />

      <details className="panel">
        <summary>Data table</summary>
        <DataTable trace={simClient.trace} version={traceVersion} />
      </details>
      <details className="panel">
        <summary>Saved runs ({runs.length})</summary>
        <RunsPanel />
      </details>
    </div>
  );
}
